// §7.6.5 Slice 4 — archive viewer page.
//
// Route: /admin/archive
// Roles: admin | customer_admin | medflow_admin
//
// Layout:
//   PageHeader "Archive & Retention"
//   Overview card   — summed stats across all entity types
//   TabBar          — 12 entity types
//   DataTable       — archived rows for selected entity type

import { useState, useEffect, useCallback } from 'react'
import { api, ApiError } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Alert,
  TabBar,
  ColumnSettingsButton,
  card,
  colors,
  btnPrimary,
  btnSecondary,
  btnDanger,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { ARCHIVE_ENTITY_TYPES } from '@alpha-edge/shared'
import type { ArchiveEntityType, ArchiveStatsEntry } from '@alpha-edge/shared'
import { ArchivePurgeConfirmModal } from './ArchivePurgeConfirmModal'
import { useTableColumns } from '../../hooks/use-table-columns'

// ---------------------------------------------------------------------------
// Display labels for each entity type
// ---------------------------------------------------------------------------
const ENTITY_TYPE_LABELS: Record<ArchiveEntityType, string> = {
  patient: 'Patients',
  order: 'Orders',
  result: 'Results',
  provider: 'Providers',
  lab_client: 'Lab Clients',
  panel: 'Panels',
  test_type: 'Test Types',
  reference_range: 'Reference Ranges',
  payer: 'Payers',
  reference_lab: 'Reference Labs',
  device: 'Devices',
  user: 'Users',
}

// ---------------------------------------------------------------------------
// Per-entity identifier formatters
// ---------------------------------------------------------------------------
export const IDENTIFIER_FORMATTERS: Record<ArchiveEntityType, (row: Record<string, unknown>) => string> = {
  patient: (r) => {
    const last = r.lastName ?? r.last_name ?? ''
    const first = r.firstName ?? r.first_name ?? ''
    const mrn = r.medicalRecordNumber ?? r.medical_record_number ?? ''
    return `${last}, ${first} (MRN: ${mrn})`
  },
  order: (r) => String(r.accessionNumber ?? r.accession_number ?? r.id ?? ''),
  result: (r) => String(r.accessionNumber ?? r.accession_number ?? r.id ?? ''),
  provider: (r) => {
    const last = r.lastName ?? r.last_name ?? ''
    const first = r.firstName ?? r.first_name ?? ''
    return `${last}, ${first}`
  },
  lab_client: (r) => String(r.name ?? r.id ?? ''),
  panel: (r) => String(r.name ?? r.id ?? ''),
  test_type: (r) => String(r.name ?? r.id ?? ''),
  reference_range: (r) => {
    const testType = r.testType ?? r.test_type ?? r.testTypeName ?? ''
    const analyte = r.analyteName ?? r.analyte_name ?? r.analyte ?? ''
    if (testType && analyte) return `${testType} / ${analyte}`
    return String(testType || analyte || r.id || '')
  },
  payer: (r) => String(r.name ?? r.id ?? ''),
  reference_lab: (r) => String(r.name ?? r.id ?? ''),
  device: (r) => String(r.serialNumber ?? r.serial_number ?? r.name ?? r.id ?? ''),
  user: (r) => {
    const last = r.lastName ?? r.last_name ?? ''
    const first = r.firstName ?? r.first_name ?? ''
    const username = r.username ?? r.email ?? ''
    return `${last}, ${first} (${username})`
  },
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ArchivedRow {
  readonly id: string
  readonly archivedAt: string
  readonly archivedByName: string | null
  readonly legalHold: boolean
  readonly [key: string]: unknown
}

// 7-day purge eligibility guard
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

function purgeDisabledReason(row: ArchivedRow): string | null {
  if (row.legalHold) return 'Legal hold active'
  if (new Date(row.archivedAt).getTime() >= Date.now() - SEVEN_DAYS_MS) {
    return 'Archived less than 7 days ago'
  }
  return null
}

// ---------------------------------------------------------------------------
// Relative date formatter
// ---------------------------------------------------------------------------
function relativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days !== 1 ? 's' : ''} ago`
}

// ---------------------------------------------------------------------------
// StatsOverview card
// ---------------------------------------------------------------------------
interface StatsOverviewProps {
  readonly stats: readonly ArchiveStatsEntry[]
  readonly loading: boolean
}

function StatsOverview({ stats, loading }: StatsOverviewProps) {
  const totalArchived = stats.reduce((s, e) => s + e.total, 0)
  const totalLegalHold = stats.reduce((s, e) => s + e.legalHold, 0)
  // purgeable is not in the archiveStatsEntrySchema directly, but the
  // backend-computed field is included as a number in the response.
  const totalPurgeable = stats.reduce((s, e) => s + ((e as unknown as Record<string, number>).purgeable ?? 0), 0)

  if (loading) {
    return <div style={{ ...card, marginBottom: '1rem', color: colors.textMuted }}>Loading stats…</div>
  }

  return (
    <div style={{ ...card, marginBottom: '1rem' }}>
      <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem' }}>
        {totalArchived} archived &middot; {totalLegalHold} on legal hold &middot; {totalPurgeable} eligible for purge
      </div>
      {stats.length === 0 ? (
        <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: 0 }}>No archived records for this org.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
          {stats
            .filter((e) => e.total > 0)
            .map((e) => (
              <span
                key={e.entityType}
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: colors.bgMuted,
                  border: `1px solid ${colors.border}`,
                  fontSize: '0.8125rem',
                  color: colors.text,
                }}
              >
                {ENTITY_TYPE_LABELS[e.entityType]}: {e.total}
                {e.legalHold > 0 && (
                  <span style={{ color: colors.danger, marginLeft: '4px' }}>({e.legalHold} hold)</span>
                )}
              </span>
            ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ArchivePage
// ---------------------------------------------------------------------------
export function ArchivePage() {
  const [activeType, setActiveType] = useState<ArchiveEntityType>('patient')
  const [stats, setStats] = useState<ArchiveStatsEntry[]>([])
  const [statsLoading, setStatsLoading] = useState(true)
  const { tableId: _, ...tableColumnProps } = useTableColumns('archive')

  const [rows, setRows] = useState<ArchivedRow[]>([])
  const [rowsLoading, setRowsLoading] = useState(true)
  const [rowsError, setRowsError] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)

  // purge modal state
  const [purgeTarget, setPurgeTarget] = useState<ArchivedRow | null>(null)
  const [purgeOpen, setPurgeOpen] = useState(false)

  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const res = await api.get<{ stats: ArchiveStatsEntry[] }>('/admin/archive/stats')
      setStats(res.data?.stats ?? [])
    } catch {
      // Non-fatal: stats card shows nothing but the list can still work
      setStats([])
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const fetchRows = useCallback(async (entityType: ArchiveEntityType) => {
    setRowsLoading(true)
    setRowsError(null)
    try {
      const res = await api.get<{ rows: ArchivedRow[] }>(`/admin/archive/${entityType}`)
      setRows((res.data?.rows ?? []) as ArchivedRow[])
    } catch (err) {
      setRowsError(err instanceof ApiError ? err.message : 'Failed to load archived records')
      setRows([])
    } finally {
      setRowsLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchStats()
  }, [fetchStats])

  useEffect(() => {
    void fetchRows(activeType)
  }, [fetchRows, activeType])

  const handleTabChange = (key: string) => {
    setActiveType(key as ArchiveEntityType)
  }

  async function handleRestore(row: ArchivedRow) {
    setPageError(null)
    try {
      await api.post(`/admin/archive/${activeType}/${row.id}/restore`, {})
      await Promise.all([fetchStats(), fetchRows(activeType)])
    } catch (err) {
      setPageError(err instanceof ApiError ? err.message : 'Restore failed')
    }
  }

  async function handleLegalHoldToggle(row: ArchivedRow) {
    setPageError(null)
    try {
      await api.post(`/admin/archive/${activeType}/${row.id}/legal-hold`, { enabled: !row.legalHold })
      await Promise.all([fetchStats(), fetchRows(activeType)])
    } catch (err) {
      setPageError(err instanceof ApiError ? err.message : 'Legal hold update failed')
    }
  }

  function openPurge(row: ArchivedRow) {
    setPurgeTarget(row)
    setPurgeOpen(true)
  }

  async function handlePurgeConfirm() {
    if (!purgeTarget) return
    // Allow the ApiError to propagate to ArchivePurgeConfirmModal, which
    // displays 409 reasons inline without closing.
    await api.delete(`/admin/archive/${activeType}/${purgeTarget.id}`)
    setPurgeOpen(false)
    setPurgeTarget(null)
    await Promise.all([fetchStats(), fetchRows(activeType)])
  }

  const identifier = (row: ArchivedRow): string => IDENTIFIER_FORMATTERS[activeType](row as Record<string, unknown>)

  const columns: readonly Column<ArchivedRow>[] = [
    {
      key: 'identifier',
      header: 'Identifier',
      sortable: true,
      render: (r) => <span style={{ fontWeight: 500 }}>{identifier(r)}</span>,
    },
    {
      key: 'archivedAt',
      header: 'Archived at',
      sortable: true,
      render: (r) => <span title={new Date(r.archivedAt).toISOString()}>{relativeTime(r.archivedAt)}</span>,
    },
    {
      key: 'archivedByName',
      header: 'Archived by',
      render: (r) => r.archivedByName ?? '—',
    },
    {
      key: 'legalHold',
      header: 'Legal hold',
      render: (r) =>
        r.legalHold ? (
          <span
            style={{
              display: 'inline-block',
              padding: '1px 6px',
              borderRadius: '3px',
              background: '#fef3c7',
              color: '#92400e',
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid #fcd34d',
            }}
          >
            Hold
          </span>
        ) : null,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => {
        const reason = purgeDisabledReason(r)
        const canPurge = reason === null
        return (
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', minWidth: '220px' }}>
            <button
              type="button"
              style={{ ...btnPrimary, fontSize: '0.75rem', padding: '2px 8px' }}
              onClick={(e) => {
                e.stopPropagation()
                void handleRestore(r)
              }}
            >
              Restore
            </button>
            <button
              type="button"
              style={{
                ...btnSecondary,
                fontSize: '0.75rem',
                padding: '2px 8px',
                background: r.legalHold ? colors.warning : colors.text,
              }}
              title={r.legalHold ? 'Remove legal hold' : 'Set legal hold'}
              onClick={(e) => {
                e.stopPropagation()
                void handleLegalHoldToggle(r)
              }}
            >
              {r.legalHold ? 'Release Hold' : 'Legal Hold'}
            </button>
            <button
              type="button"
              style={{
                ...btnDanger,
                fontSize: '0.75rem',
                padding: '2px 8px',
                opacity: canPurge ? 1 : 0.4,
                cursor: canPurge ? 'pointer' : 'not-allowed',
              }}
              disabled={!canPurge}
              title={reason ?? undefined}
              onClick={(e) => {
                e.stopPropagation()
                if (canPurge) openPurge(r)
              }}
            >
              Purge Now
            </button>
          </div>
        )
      },
    },
  ]

  const tabs = ARCHIVE_ENTITY_TYPES.map((t) => ({ key: t, label: ENTITY_TYPE_LABELS[t] }))

  return (
    <div>
      <PageHeader
        title="Archive & Retention"
        subtitle="Manage archived records and retention policy"
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      {pageError && <Alert type="error" message={pageError} />}

      <StatsOverview stats={stats} loading={statsLoading} />

      <div style={{ marginBottom: '0' }}>
        <TabBar tabs={tabs} activeTab={activeType} onTabChange={handleTabChange} />
      </div>

      <div style={{ marginTop: '1rem' }}>
        {rowsError && <Alert type="error" message={rowsError} />}
        {rowsLoading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage={`No archived ${ENTITY_TYPE_LABELS[activeType].toLowerCase()} for this org.`}
            {...tableColumnProps}
          />
        )}
      </div>

      {purgeTarget && (
        <ArchivePurgeConfirmModal
          isOpen={purgeOpen}
          onClose={() => {
            setPurgeOpen(false)
            setPurgeTarget(null)
          }}
          onConfirm={handlePurgeConfirm}
          entityType={activeType}
          entityId={purgeTarget.id}
          identifier={identifier(purgeTarget)}
          archivedAt={purgeTarget.archivedAt}
          rowSnapshot={purgeTarget as Record<string, unknown>}
        />
      )}
    </div>
  )
}
